(() => {
  "use strict";

  const configElement = document.getElementById("auto-cart-rules-config");

  if (!configElement) {
    return;
  }

  let rules = [];

  try {
    rules = JSON.parse(configElement.textContent.trim());
  } catch (error) {
    console.error("[Auto cart rules] Invalid JSON:", error);
    return;
  }

  rules = rules
    .filter((rule) => {
      return (
        Array.isArray(rule?.triggerVariantIds) &&
        rule.triggerVariantIds.length &&
        rule?.linkedVariantId
      );
    })
    .map((rule) => ({
      triggerVariantIds: rule.triggerVariantIds.map(String),
      linkedVariantId: String(rule.linkedVariantId),
      quantity: Math.max(1, Number(rule.quantity) || 1),
      matchQuantity: Boolean(rule.matchQuantity),
      onlyIfMissing: rule.onlyIfMissing !== false
    }));

  if (!rules.length) {
    return;
  }

  const rootUrl = window.Shopify?.routes?.root || "/";
  const cartUrl = `${rootUrl}cart.js`;
  const cartAddUrl = `${rootUrl}cart/add.js`;

  let isHandlingAutoAdd = false;
  const PENDING_KEY = "auto-cart-rules:pending-trigger";
const PENDING_MAX_AGE = 30000;

  const getVariantId = (item) => {
    return String(item?.variant_id ?? item?.id ?? "");
  };

  async function getCart() {
    const response = await fetch(cartUrl, {
      headers: {
        Accept: "application/json"
      }
    });

    if (!response.ok) {
      throw new Error("Could not retrieve the cart.");
    }

    return response.json();
  }

  function getSectionsToRefresh() {
    const sections = ["variant-added"];

    // Impact listens on document, but the event must start on <html>
    // so it bubbles through the document listener.
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:prepare-bundled-sections", {
        bubbles: true,
        detail: { sections }
      })
    );

    return [...new Set(sections)];
  }

  function getMatchingRules(variantId) {
    return rules.filter((rule) =>
      rule.triggerVariantIds.includes(String(variantId))
    );
  }

  function getSubmitButtons(form) {
    return [...form.elements].filter(
      (element) => element instanceof HTMLButtonElement && element.type === "submit"
    );
  }

  async function addLinkedItems(triggerVariantId, triggerQuantity, sections) {
    const applicableRules = getMatchingRules(triggerVariantId);

    if (!applicableRules.length) {
      return null;
    }

    const cart = await getCart();

    const variantsAlreadyInCart = new Set(
      cart.items.map((item) => getVariantId(item))
    );

    const linkedItems = new Map();

    for (const rule of applicableRules) {
      if (
        rule.onlyIfMissing &&
        (variantsAlreadyInCart.has(rule.linkedVariantId) ||
          linkedItems.has(rule.linkedVariantId))
      ) {
        continue;
      }

      const quantity = rule.matchQuantity
        ? rule.quantity * triggerQuantity
        : rule.quantity;

      linkedItems.set(
        rule.linkedVariantId,
        (linkedItems.get(rule.linkedVariantId) || 0) + quantity
      );
    }

    if (!linkedItems.size) {
      return null;
    }

    const response = await fetch(cartAddUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest"
      },
      body: JSON.stringify({
        items: [...linkedItems.entries()].map(([id, quantity]) => ({
          id,
          quantity
        })),
        sections: sections.join(","),
        sections_url: `${window.location.pathname}${window.location.search}`
      })
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.description || "Could not add linked product.");
    }

    return result;
  }

  function rememberPendingTrigger(variantId, quantity = 1) {
  const cleanVariantId = String(variantId || "");

  if (!cleanVariantId || !getMatchingRules(cleanVariantId).length) {
    return;
  }

  sessionStorage.setItem(
    PENDING_KEY,
    JSON.stringify({
      variantId: cleanVariantId,
      quantity: Math.max(1, Number(quantity) || 1),
      createdAt: Date.now()
    })
  );
}

function rememberTriggerFromForm(form) {
  if (!(form instanceof HTMLFormElement)) {
    return;
  }

  const formData = new FormData(form);
  rememberPendingTrigger(
    formData.get("id"),
    formData.get("quantity")
  );
}

/* Covers ordinary product forms and custom buttons attached to forms. */
document.addEventListener(
  "click",
  (event) => {
    const control = event.target.closest("button, input[type='submit'], a[href]");

    if (!control) {
      return;
    }

    const form =
      control.form instanceof HTMLFormElement
        ? control.form
        : control.closest("form");

    if (form) {
      rememberTriggerFromForm(form);
      return;
    }

    /* Covers direct cart links such as /cart/123456789:1 */
    const link = control.closest("a[href]");

    if (!link) {
      return;
    }

    const url = new URL(link.href, window.location.href);
    const cartLinkMatch = url.pathname.match(/\/cart\/(\d+):(\d+)/);

    if (cartLinkMatch) {
      rememberPendingTrigger(cartLinkMatch[1], cartLinkMatch[2]);
      return;
    }

    /* Covers custom buttons carrying their variant ID in data attributes. */
    const variantCarrier = control.closest(
      "[data-variant-id], [data-product-variant-id]"
    );

    if (variantCarrier) {
      rememberPendingTrigger(
        variantCarrier.dataset.variantId ||
          variantCarrier.dataset.productVariantId,
        variantCarrier.dataset.quantity || 1
      );
    }
  },
  true
);

document.addEventListener(
  "submit",
  (event) => rememberTriggerFromForm(event.target),
  true
);

  /*
    Capturing phase deliberately runs before Impact's native product-form
    submit handler. We take over only when a configured trigger variant is added.
  */
  document.addEventListener(
    "submit",
    async (event) => {
      const form = event.target;

      if (!(form instanceof HTMLFormElement)) {
        return;
      }

      if (isHandlingAutoAdd) {
        return;
      }

      const formData = new FormData(form);
      const triggerVariantId = String(formData.get("id") || "");

      if (!triggerVariantId || !getMatchingRules(triggerVariantId).length) {
        return;
      }

      // Do not interfere with non-cart submits or checkout buttons.
      if (event.submitter?.name === "checkout") {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      isHandlingAutoAdd = true;

      const submitButtons = getSubmitButtons(form);
      submitButtons.forEach((button) => button.setAttribute("aria-busy", "true"));

      try {
        const sections = getSectionsToRefresh();

        const triggerQuantity = Math.max(
          1,
          Number(formData.get("quantity")) || 1
        );

        // Recreate Impact's normal primary product add.
        formData.set("sections", sections.join(","));
        formData.set(
          "sections_url",
          `${rootUrl}variants/${triggerVariantId}`
        );

        const primaryResponse = await fetch(cartAddUrl, {
          method: "POST",
          body: formData,
          headers: {
            "X-Requested-With": "XMLHttpRequest"
          }
        });

        const primaryResult = await primaryResponse.json();

        if (!primaryResponse.ok) {
          throw new Error(
            primaryResult.description || "Could not add product to cart."
          );
        }

        // Add every linked product before refreshing the visible cart drawer.
        const linkedResult = await addLinkedItems(
          triggerVariantId,
          triggerQuantity,
          sections
        );

        const finalCart = await getCart();

        // The final request includes the proper cart-drawer section HTML.
        finalCart.sections =
          linkedResult?.sections || primaryResult.sections || {};

        // Impact opens the cart drawer when this bubbles from the form.
        form.dispatchEvent(
          new CustomEvent("variant:add", {
            bubbles: true,
            detail: {
              items: primaryResult.items
                ? primaryResult.items
                : [primaryResult],
              cart: finalCart,
              autoCartRule: true
            }
          })
        );

        // Impact replaces the cart drawer using the bundled section HTML.
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:change", {
            bubbles: true,
            detail: {
              baseEvent: "variant:add",
              cart: finalCart
            }
          })
        );
      } catch (error) {
        console.error("[Auto cart rules]", error);

        form.dispatchEvent(
          new CustomEvent("cart:error", {
            bubbles: true,
            detail: {
              error: error.message || "Could not add product to cart."
            }
          })
        );
      } finally {
  sessionStorage.removeItem(PENDING_KEY);

  submitButtons.forEach((button) =>
    button.removeAttribute("aria-busy")
  );

  isHandlingAutoAdd = false;
}
    },
    true
  );

  async function repairRedirectedCart() {
  const cartPath = `${rootUrl.replace(/\/$/, "")}/cart`;
  const currentPath = window.location.pathname.replace(/\/$/, "");

  if (currentPath !== cartPath.replace(/\/$/, "")) {
    return;
  }

  let pending;

  try {
    pending = JSON.parse(sessionStorage.getItem(PENDING_KEY) || "null");
  } catch (_) {
    sessionStorage.removeItem(PENDING_KEY);
    return;
  }

  if (
    !pending?.variantId ||
    Date.now() - Number(pending.createdAt || 0) > PENDING_MAX_AGE
  ) {
    sessionStorage.removeItem(PENDING_KEY);
    return;
  }

  try {
    const triggerVariantId = String(pending.variantId);
    const triggerQuantity = Math.max(1, Number(pending.quantity) || 1);
    const cart = await getCart();

    const triggerExists = cart.items.some(
      (item) => getVariantId(item) === triggerVariantId
    );

    if (!triggerExists) {
      sessionStorage.removeItem(PENDING_KEY);
      return;
    }

    const variantsAlreadyInCart = new Set(
      cart.items.map((item) => getVariantId(item))
    );

    const itemsToAdd = new Map();

    for (const rule of getMatchingRules(triggerVariantId)) {
      if (rule.linkedVariantId === triggerVariantId) {
        continue;
      }

      if (
        rule.onlyIfMissing &&
        (variantsAlreadyInCart.has(rule.linkedVariantId) ||
          itemsToAdd.has(rule.linkedVariantId))
      ) {
        continue;
      }

      const quantity = rule.matchQuantity
        ? rule.quantity * triggerQuantity
        : rule.quantity;

      itemsToAdd.set(
        rule.linkedVariantId,
        (itemsToAdd.get(rule.linkedVariantId) || 0) + quantity
      );
    }

    if (!itemsToAdd.size) {
      sessionStorage.removeItem(PENDING_KEY);
      return;
    }

    const response = await fetch(cartAddUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest"
      },
      body: JSON.stringify({
        items: [...itemsToAdd.entries()].map(([id, quantity]) => ({
          id,
          quantity
        }))
      })
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.description || "Could not add linked product.");
    }

    sessionStorage.removeItem(PENDING_KEY);

    /* Reload once, now with both items present. */
    window.location.replace(`${rootUrl}cart`);
  } catch (error) {
    sessionStorage.removeItem(PENDING_KEY);
    console.error("[Auto cart rules] Cart-page recovery failed:", error);
  }
}

repairRedirectedCart();
})();